import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Trash2 } from "lucide-react";
import { toast } from "sonner";
import { UploadBookDialog } from "@/components/UploadBookDialog";
import { useAuth } from "@/context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const slugify = (name) => name.toLowerCase().replace(/[^a-z0-9]+/g, "-");

const BookCard = ({ book, onOpenBook, onDelete }) => (
  <button
    data-testid={`book-card-${slugify(book.name)}`}
    onClick={() => onOpenBook(book)}
    className="group text-left"
  >
    <div className="relative aspect-[3/4] overflow-hidden rounded-md border border-border bg-card shadow-sm transition-[transform,box-shadow] duration-300 group-hover:-translate-y-1 group-hover:shadow-[0_16px_40px_rgba(0,0,0,0.35)]">
      <img
        src={`${BACKEND_URL}${book.cover}`}
        alt={`Cover of ${book.title}`}
        loading="lazy"
        className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
      />
      <span className="absolute bottom-2 right-2 rounded-full bg-background/85 px-2 py-0.5 text-[10px] uppercase tracking-wider text-foreground backdrop-blur-sm">
        {book.pageCount} pages
      </span>
      {book.source === "cloud" && onDelete && (
        <span
          role="button"
          tabIndex={0}
          data-testid={`book-delete-${slugify(book.name)}`}
          onClick={(e) => { e.stopPropagation(); onDelete(book); }}
          onKeyDown={(e) => { if (e.key === "Enter") { e.stopPropagation(); onDelete(book); } }}
          className="absolute right-2 top-2 rounded-full bg-background/85 p-1.5 text-muted-foreground opacity-0 backdrop-blur-sm transition-opacity duration-200 hover:text-destructive group-hover:opacity-100"
        >
          <Trash2 className="h-3.5 w-3.5" />
        </span>
      )}
    </div>
    <h3 className="mt-3 font-display text-sm font-semibold leading-snug">{book.title}</h3>
    {book.blurb && (
      <p className="mt-1 line-clamp-2 text-xs leading-relaxed text-muted-foreground">{book.blurb}</p>
    )}
  </button>
);

export const Bookshelf = ({ onOpenBook }) => {
  const { user } = useAuth();
  const [books, setBooks] = useState(null);
  const [error, setError] = useState(null);

  const load = useCallback(() => {
    axios
      .get(`${API}/books`, { withCredentials: false })
      .then((res) => { setBooks(res.data.books); setError(null); })
      .catch(() => setError("The shelf could not be reached. Try again shortly."));
  }, []);

  useEffect(() => { load(); }, [load]);

  const deleteBook = async (book) => {
    try {
      await axios.delete(`${API}/books/cloud/${book.id}`);
      toast.success(`"${book.title}" removed from the shelf`);
      load();
    } catch {
      toast.error("Could not remove that tome");
    }
  };

  return (
    <section id="shelf" className="mx-auto max-w-6xl scroll-mt-24 px-6 py-24 md:py-32">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.3em] text-primary">The shelf</p>
          <h2 className="mt-3 font-display text-2xl font-semibold tracking-tight sm:text-3xl">
            Choose a tome and settle in
          </h2>
        </div>
        <div className="flex items-center gap-4">
          {books && (
            <p data-testid="shelf-count" className="text-xs uppercase tracking-[0.2em] text-muted-foreground">
              {books.length} tomes catalogued
            </p>
          )}
          {user ? (
            <UploadBookDialog onUploaded={load} />
          ) : (
            <p data-testid="signin-hint" className="text-xs uppercase tracking-[0.2em] text-muted-foreground">
              Sign in to add tomes
            </p>
          )}
        </div>
      </div>
      {error && (
        <p data-testid="shelf-error" className="mt-10 text-sm text-destructive">
          {error}
        </p>
      )}
      {!books && !error && (
        <div className="mt-12 grid grid-cols-2 gap-6 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 md:gap-8">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="aspect-[3/4] animate-pulse rounded-md border border-border bg-muted" />
          ))}
        </div>
      )}
      {books && (
        <div
          data-testid="bookshelf-grid"
          className="mt-12 grid grid-cols-2 gap-6 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 md:gap-8"
        >
          {books.map((book) => (
            <BookCard key={book.id || book.name} book={book} onOpenBook={onOpenBook} onDelete={user ? deleteBook : undefined} />
          ))}
        </div>
      )}
    </section>
  );
};
